import {
    AdProvider,
    type AdProviderConfig,
    AdProviderStatus,
    type AdUnitProvidersConfig,
    type OutOfPageAdType,
    type PageAdOptions,
    type PageAdSize,
    type PageAdType,
    isOutOfPageAdType,
} from '@/modules/adv'
import { Banner, BannerState, BannerType } from '@/modules/adv/banner'
import { HeaderBidding } from '@/modules/adv/header-bidding'
import type { HeaderBiddingConfig } from '@/modules/adv/header-bidding/types'
import { OutOfPageBanner } from '@/modules/adv/out-of-page-banner'
import { DebugAdsProvider } from '@/modules/adv/providers/debug-provider'
import { DirectAdsProvider } from '@/modules/adv/providers/direct'
import { EmptyAdsProvider } from '@/modules/adv/providers/empty-ads'
import { GameDistributionConnector } from '@/modules/adv/providers/gd-connector/gd-connector'
import { GoogleAdSenseAdProvider } from '@/modules/adv/providers/google-ad-sense-ad-provider'
import { GoogleAdsService } from '@/modules/adv/providers/google-ads'
import { GoogleBannerProvider } from '@/modules/adv/providers/google-banner'
import { GoogleInstreamAdsService } from '@/modules/adv/providers/google-instream-ads'
import { AdProviderNames, type PlatformType } from '@/types'
import type { EventForLoggersType, RumMetricData, RumMetricResult } from '@/types/logger.types'
import { timer } from '@/utils/helpers'

export type AdServiceOptions = {
    providerKeys: AdProviderNames[]
    targeting?: Record<string, string | string[]>
    logEvent: (event: EventForLoggersType) => void
    startRumEvent: (event: RumMetricData) => (event: RumMetricResult) => void
    initialDelay?: Promise<void>
    headerBiddingConfig?: HeaderBiddingConfig
    adRequestTimeoutMs: number
    gamAccount: string
    platform: PlatformType
    auid: string
    isWidget: boolean

    adUnitProviderConfig?: Partial<AdUnitProvidersConfig<AdProviderNames>>
    footerHeight?: string
    locale: string
}

const PROVIDER_INITIALIZATION_TIMEOUT = 5000 // 5 seconds
const MEMBRANA_GAM = '22308795431'

export class AdService {
    private readonly options: AdServiceOptions

    private readonly logEvent: (event: EventForLoggersType) => void

    private readonly startRumEvent: (event: RumMetricData) => (event: RumMetricResult) => void

    private waitingProviders: AdProvider[] | null = null

    private providers: AdProvider[] = []

    private enabled = true

    private headerBidding: HeaderBidding | null = null

    currentOutOfPageAdWaterfallIndex = 0

    serviceReadyPromise: Promise<AdProviderStatus>

    banners: Set<Banner | OutOfPageBanner> = new Set()

    constructor(options: AdServiceOptions) {
        this.options = options
        this.logEvent = options.logEvent
        this.startRumEvent = options.startRumEvent

        this.serviceReadyPromise = (options.initialDelay || Promise.resolve()).then(() => this.initProviders())
    }

    private async initProviders(): Promise<AdProviderStatus> {
        const sendRumEvent = this.startRumEvent({
            operationName: 'AdService_initProviders',
            details: this.options.providerKeys.join(','),
        })

        this.waitingProviders = this.options.providerKeys.map((key) => this.getProvider(key, this.options))

        if (this.waitingProviders.length === 0) {
            sendRumEvent({
                result: 'Error: No providers defined',
            })
            throw new Error('No providers defined')
        }

        const initializers: Array<Promise<unknown>> = []

        if (this.options.headerBiddingConfig) {
            this.headerBidding = new HeaderBidding(this.options.headerBiddingConfig)
            initializers.push(this.headerBidding.init())
        }

        const successProviders: string[] = []

        initializers.push(
            ...this.waitingProviders.map((provider, index) =>
                Promise.race([
                    provider.serviceStatus.then((status) => {
                        if (status === AdProviderStatus.online) {
                            this.providers[index] = provider
                            successProviders.push(this.options.providerKeys[index])
                        }
                    }),
                    timer(PROVIDER_INITIALIZATION_TIMEOUT),
                ]),
            ),
        )

        await Promise.all(initializers).then(() => {
            if (successProviders.length === this.options.providerKeys.length) {
                sendRumEvent({
                    result: `Success: All providers were defined`,
                })
            } else {
                sendRumEvent({
                    result: `Success: ${successProviders.join(',')} providers among ${this.options.providerKeys.join(',')} were defined'`,
                })
            }
        })

        if (this.providers.length === 0) {
            sendRumEvent({
                result: 'Error: No providers loaded',
            })
            this.providers = [new EmptyAdsProvider(this.options)]
        }

        return AdProviderStatus.online
    }

    private getProvider(key: AdProviderNames, adServiceConfig: AdServiceOptions) {
        const PROVIDER_CREATORS: Record<AdProviderNames, () => AdProvider> = {
            [AdProviderNames.GAME_DISTRIBUTION]: () => new GameDistributionConnector(),
            [AdProviderNames.DEBUG_PROVIDER]: () => new DebugAdsProvider(),
            [AdProviderNames.EMPTY_PROVIDER]: () =>
                new EmptyAdsProvider({
                    ...adServiceConfig,
                }),
            [AdProviderNames.GOOGLE_INSTREAM_HIGH]: () =>
                new GoogleInstreamAdsService({
                    ...adServiceConfig,
                    targeting: { ...(adServiceConfig.targeting || {}), segment: 'premium' },
                    adUnitConfig: {
                        desktop: {
                            interstitial_preroll: 'instream_preroll_high_floor_desktop',
                            interstitial: 'instream_high_floor_desktop',
                            rewarded: 'instream_rewarded_high_floor_desktop',
                        },
                        mobile: {
                            interstitial_preroll: 'instream_preroll_high_floor_mobile',
                            interstitial: 'instream_high_floor_mobile',
                            rewarded: 'instream_rewarded_high_floor_mobile',
                        },
                    },
                    gamAccount: this.options.gamAccount,
                    useOnlyConfig: true,
                    type: AdProviderNames.GOOGLE_INSTREAM_HIGH,
                }),
            [AdProviderNames.GOOGLE_INSTREAM]: () =>
                new GoogleInstreamAdsService({
                    ...adServiceConfig,
                    adUnitConfig: adServiceConfig.adUnitProviderConfig?.[AdProviderNames.GOOGLE_INSTREAM],
                    gamAccount: this.options.gamAccount,
                }),
            [AdProviderNames.GOOGLE_INSTREAM_CHEAP]: () =>
                new GoogleInstreamAdsService({
                    ...adServiceConfig,
                    adUnitConfig: adServiceConfig.adUnitProviderConfig?.[AdProviderNames.GOOGLE_INSTREAM_CHEAP],
                    gamAccount: this.options.gamAccount,
                    type: AdProviderNames.GOOGLE_INSTREAM_CHEAP,
                    logEvent: this.logEvent,
                }),
            [AdProviderNames.MEMBRANA]: () =>
                new GoogleAdsService({
                    ...adServiceConfig,
                    adUnitConfig: {
                        desktop: {
                            rewarded: 'PGM_REWARD',
                            interstitial: 'PGM_INTERSTITIAL',
                        },
                        mobile: {
                            rewarded: 'PGM_REWARD',
                            interstitial: 'PGM_INTERSTITIAL',
                        },
                    },
                    gamAccount: MEMBRANA_GAM,
                    useOnlyConfig: true,
                }),
            [AdProviderNames.MEMBRANA_VAST]: () =>
                new GoogleInstreamAdsService({
                    ...adServiceConfig,
                    adUnitConfig: {
                        desktop: {
                            interstitial_preroll: 'PGM_PREROLL',
                        },
                        mobile: {
                            interstitial_preroll: 'PGM_PREROLL',
                        },
                    },
                    gamAccount: MEMBRANA_GAM,
                    adUrl: 'https://server.smartytech.io/vast?id=204',
                    useOnlyConfig: true,
                }),
            [AdProviderNames.GOOGLE_AD_SENSE]: () => new GoogleAdSenseAdProvider(adServiceConfig),
            [AdProviderNames.GOOGLE_AD]: () =>
                new GoogleAdsService({
                    ...adServiceConfig,
                    adUnitConfig: adServiceConfig.adUnitProviderConfig?.[AdProviderNames.GOOGLE_AD],
                    gamAccount: this.options.gamAccount,
                }),
            [AdProviderNames.DIRECT]: () =>
                new DirectAdsProvider({
                    ...adServiceConfig,
                    logEvent: this.logEvent,
                }),
            [AdProviderNames.GOOGLE_BANNER]: () =>
                new GoogleBannerProvider({
                    ...adServiceConfig,
                    adUnitConfig: adServiceConfig.adUnitProviderConfig?.[AdProviderNames.GOOGLE_AD],
                    gamAccount: this.options.gamAccount,
                }),
        }

        const providerCreator: () => AdProvider = PROVIDER_CREATORS[key] || PROVIDER_CREATORS[AdProviderNames.GOOGLE_AD]

        return providerCreator()
    }

    private requestAd<T extends Banner>(
        providerMethod: (provider: AdProvider, bannerTargeting: AdProviderConfig['targeting']) => Promise<T>,
        fallbackBanner: () => T,
        {
            type,
            sizes,
        }: {
            type: PageAdType | OutOfPageAdType
            sizes?: PageAdSize[]
        },
        waterfallIndex = 0,
    ): Promise<T> {
        if (isOutOfPageAdType(type)) {
            this.currentOutOfPageAdWaterfallIndex = waterfallIndex
        }

        // eslint-disable-next-line no-async-promise-executor
        return new Promise(async (r) => {
            const currentProviderName: AdProviderNames = this.options.providerKeys[waterfallIndex]
            const sendRumEvent = this.startRumEvent({
                operationName: 'AdService_requestAd',
                details: `${type}_${currentProviderName}`,
            })
            const resolve = (banner: T) => {
                this.currentOutOfPageAdWaterfallIndex = 0
                r(banner)
            }

            try {
                const currentProvider = this.providers[waterfallIndex]

                if (!currentProvider) {
                    sendRumEvent({
                        result: 'Error: Provider is not initialized',
                    })
                    throw new Error('Provider is not initialized')
                }

                this.logEvent({
                    event: 'custom_event',
                    eventName: 'ad_request',
                    adPlacement: type,
                    action: 'start',
                    adUnit: currentProvider.getAdUnitByType(type),
                    waterfallIndex: waterfallIndex + 1,
                })

                let prebidTargeting: AdProviderConfig['targeting']
                if (currentProvider.hasPrebidIntegration && this.headerBidding) {
                    prebidTargeting = await this.headerBidding.runAuction(
                        currentProvider.getAdUnitByType(type) || type,
                        { type, sizes },
                    )
                }
                const banner = await providerMethod(currentProvider, prebidTargeting)
                this.banners.add(banner)

                banner.addEventListener('empty', () => {
                    sendRumEvent({
                        result: `Error: ${type} is empty`,
                    })
                    if (waterfallIndex + 1 === this.providers.length) {
                        resolve(banner)
                    } else {
                        this.requestAd(
                            providerMethod,
                            fallbackBanner,
                            {
                                type,
                                sizes,
                            },
                            waterfallIndex + 1,
                        ).then(resolve)
                    }
                })

                if (banner.state === BannerState.ready) {
                    resolve(banner)
                } else if (banner.state === BannerState.closed || banner.state === BannerState.empty) {
                    throw new Error(`Ad is in state=[${banner.state}]`)
                } else {
                    banner.addEventListener('ready', () => {
                        sendRumEvent({
                            result: `Success: ${type} is ready`,
                        })
                        resolve(banner)
                    })
                }

                banner.addEventListener('closed', () => {
                    resolve(banner)
                    this.banners.delete(banner)
                })

                // for page ad we don't have ready event
                banner.addEventListener('rendered', () => {
                    sendRumEvent({
                        result: `Success: ${type} is rendered`,
                    })
                    resolve(banner)
                })

                banner.addEventListener('viewable', () => {
                    this.logEvent({
                        event: 'custom_event',
                        eventName: 'ad_show',
                        adPlacement: type,
                        action: 'show',
                        adUnit: banner.adUnit,
                        instreamDuration: banner.instreamDuration,
                    })
                })
            } catch (error) {
                if (waterfallIndex + 1 === this.providers.length) {
                    const banner = fallbackBanner()
                    resolve(banner)
                    banner.triggerEmpty()
                    const errorMessage = error instanceof Error ? error.message : `Cannot show the ad ${type}`
                    sendRumEvent({
                        result: errorMessage,
                    })
                    this.logEvent({
                        event: 'custom_event',
                        eventName: 'ad_request_failed',
                        adPlacement: type,
                        action: 'error',
                        errorMessage,
                    })
                } else {
                    this.requestAd(
                        providerMethod,
                        fallbackBanner,
                        {
                            type,
                            sizes,
                        },
                        waterfallIndex + 1,
                    ).then(resolve)
                }
            }
        })
    }

    async requestOutOfPageAd(type: OutOfPageAdType, customTimeout?: number): Promise<OutOfPageBanner> {
        const timeout = customTimeout ?? this.options.adRequestTimeoutMs
        if (!this.enabled && !['rewarded', 'rewarded_2'].includes(type)) {
            const banner = new OutOfPageBanner({ provider: AdProviderNames.EMPTY_PROVIDER })
            banner.triggerEmpty()

            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_skipped',
                adPlacement: type,
            })
            return banner
        }

        await this.serviceReadyPromise
        return this.requestAd<OutOfPageBanner>(
            (provider, bannerTargeting) => provider.prepareOutOfPageAd(type, timeout, bannerTargeting),
            () => new OutOfPageBanner({ provider: AdProviderNames.EMPTY_PROVIDER }),
            { type },
        )
    }

    async requestPageAd(options: PageAdOptions): Promise<Banner> {
        if (!this.enabled) {
            const banner = new Banner({ provider: AdProviderNames.EMPTY_PROVIDER })
            banner.triggerEmpty()

            this.logEvent({
                event: 'custom_event',
                eventName: 'ad_skipped',
                adPlacement: options.type,
            })
            return banner
        }

        await this.serviceReadyPromise
        return this.requestAd<Banner>(
            (provider, bannerTargeting) => provider.requestPageAd(options, bannerTargeting),
            () => new Banner({ provider: AdProviderNames.EMPTY_PROVIDER }),
            { type: options.type, sizes: options.sizes },
        )
    }

    setOutOfPageAdPlaybackState(play: boolean): void {
        for (const banner of this.banners) {
            if (banner.type === BannerType.outOfPageBanner) {
                ;(banner as OutOfPageBanner).setPlaybackState(play)
            }
        }
    }

    toggleState(enabled: boolean) {
        if (this.enabled !== enabled) {
            this.enabled = enabled
        }

        if (!enabled) {
            for (const banner of this.banners) {
                banner.destroy()
            }
            this.banners.clear()
        }
    }

    updateTargeting(targeting: AdProviderConfig['targeting']) {
        this.serviceReadyPromise.then(() => {
            this.waitingProviders?.forEach((provider) => {
                try {
                    provider.updateTargeting(targeting)
                } catch {
                    // provider threw an error, nothing we can do
                }
            })
        })
    }

    getAdMaxTimeout() {
        return this.options.adRequestTimeoutMs * this.options.providerKeys.length
    }
}
